import { Box, Container, Grid, Paper, Tab, Tabs, Typography } from "@mui/material";
import { FC, memo, useCallback, useEffect, useState } from "react";
import useSnackBars from "../../hooks/useSnackbar";
import CreateIndex from "./CreateIndex";
import CreateView from "./CreateView";
import SelectView from "./SelectView";
import Loader from "../common/Loader";

interface ResourceExplorerMainPageProps {
	arn: string;
	region: string;
	setRegion: (index: string) => void;
	selectedView: string;
	setSelectedView: (index: string) => void;
	listIndexesData: any;
	listViews: any;
	loading: boolean;
	listViewsLoading: boolean;
	refetch: any;
	disabled: boolean;
	changeTab: (
		index: number,
	) => void;
}

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

const TabPanel = memo(({ children, value, index, ...other }: TabPanelProps) => {
	return (
		<Container
			role="tabpanel"
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}
		>
			{value === index && (
				<Box sx={{ p: 3 }}>
					<Typography component="span">{children}</Typography>
				</Box>
			)}
		</Container>
	);
});

/**
 * Used for tabs
 */
function a11yProps(index: number) {
	return {
		id: `simple-tab-${index}`,
		"aria-controls": `simple-tabpanel-${index}`,
	};
}

const ResourceExplorerMainPage: FC<ResourceExplorerMainPageProps> =({
	arn,
	region,
	setRegion,
	selectedView,
	setSelectedView,
	listIndexesData,
	listViews,
	loading,
	listViewsLoading,
	refetch,
	disabled,
	changeTab
}: ResourceExplorerMainPageProps) => {
	const { setAlerts } = useSnackBars();
	const [value, setValue] = useState(0);
	const [selectViewDisabled, setSelectViewDisabled] = useState(false);
	// Handles which tab to change to
	const handleChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
		setValue(newValue);
	}, [setValue]);

	useEffect(() => {
	  if(listIndexesData?.listIndexes?.items.length === 0 || listIndexesData?.listIndexes === null){
		setValue(0);
	  } else if (listIndexesData?.listIndexes?.items.length !== 0 && listViews?.items.length === 0){
		setValue(1);
		setSelectViewDisabled(true);
	  } else {
		setValue(2);
	  }
	}, [listIndexesData, listViews])
	
	const tabPanels = [
		{
			index: 0,
			component: (
				<CreateIndex
					arn={arn}
					region={region}
					setRegion={setRegion}
					data={listIndexesData}
					loading={loading}
					refetch={refetch}
					setAlerts={setAlerts}
					changeCreateTab={(
						index: number,
					) => {
						setValue(index);
						refetch();
					}}
					changeTab={changeTab}
				/>
			),
		},
		{
			index: 1,
			component: (
				<CreateView
					arn={arn}
					region={region}
					setRegion={setRegion}
					data={listIndexesData}
					loading={loading}
					setAlerts={setAlerts}
					changeTab={changeTab}
				/>
			),
		},
		{
			index: 2,
			component: (
				<SelectView
					data={listViews}
					loading={loading}
					selectedView={selectedView}
					setSelectedView={setSelectedView}
					changeTab={changeTab}
				/>
			),
		},
		// Add placeholders for other panels if needed
	];

	if (loading || listViewsLoading) {
		return (
			<Container maxWidth="lg" component={Paper}>
				<Box sx={{ flexGrow: 1, pb: 2 }}>
					<Box sx={{ width: "100%" }}>
						<Loader />
					</Box>
				</Box>
			</Container>
		);
	}
	return (
		<Grid
			container
			sx={{ p: 2 }}
			spacing={2}
			alignItems="stretch"
			flexDirection={"column"}
			component={Paper}
			maxWidth="lg"
		>
			<Box sx={{ flexGrow: 1, pb: 2 }}>
				<Box sx={{ width: "100%" }}>
					<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
						<Tabs
							value={value}
							onChange={handleChange}
							textColor="secondary"
							indicatorColor="secondary"
							variant="fullWidth"
						>
							<Tab label="Create Index" {...a11yProps(0)} />
							<Tab label="Create View" disabled={disabled} {...a11yProps(1)} />
							<Tab label="Select View" disabled={disabled || selectViewDisabled} {...a11yProps(2)} />
						</Tabs>
					</Box>
					{tabPanels.map(({ index, component }) => (
						<TabPanel value={value} index={index} key={`MultiFramework-${index}`}>
							{component}
						</TabPanel>
					))}
				</Box>
			</Box>
		</Grid>
	);
};

export default ResourceExplorerMainPage;